import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, IconButton, Stack, Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { format, startOfWeek, addDays, isSameDay } from 'date-fns';
import { collection, query, onSnapshot, doc, setDoc, where } from 'firebase/firestore';
import { db } from 'lib/firebase';
import { useSnackbar } from 'notistack';
import MealDayCard from './MealDayCard';
import MealEditModal from './MealEditModal';

const MealPlanning = () => {
    const navigate = useNavigate();
    const [mealPlans, setMealPlans] = useState({});
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const scrollContainerRef = useRef(null);
    const { enqueueSnackbar } = useSnackbar();

    const [autoStart, setAutoStart] = useState(false);

    // Calculate current week (Monday start)
    const startDate = startOfWeek(new Date(), { weekStartsOn: 1 });
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(startDate, i));

    useEffect(() => {
        // Subscribe to meal plans starting from this week's start date
        // Format as YYYY-MM-DD to match the document ID or use a 'date' field if it exists.
        // The document structure is { date: 'YYYY-MM-DD', ... }.
        // Since we are using dockey as YYYY-MM-DD, we can mostly rely on collection fetch if small,
        // BUT the PR review specifically requested a query limit.
        // We'll query based on the 'date' field which matches the key.
        
        const startKey = format(startDate, 'yyyy-MM-dd');
        
        const q = query(
            collection(db, 'meal_plans'),
            where('date', '>=', startKey)
        );
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const plans = {};
            snapshot.forEach(doc => {
                plans[doc.id] = doc.data();
            });
            setMealPlans(plans);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const handleBack = () => {
        navigate('/family-central');
    };

    // Drag to scroll logic
    const isDown = useRef(false);
    const startX = useRef(0);
    const scrollLeft = useRef(0);
    const isDragging = useRef(false);

    const onMouseDown = (e) => {
        isDown.current = true;
        isDragging.current = false;
        scrollContainerRef.current.classList.add('active');
        startX.current = e.pageX - scrollContainerRef.current.offsetLeft;
        scrollLeft.current = scrollContainerRef.current.scrollLeft;
        scrollContainerRef.current.style.cursor = 'grabbing';
    };

    const onMouseLeave = () => {
        isDown.current = false;
        isDragging.current = false;
        scrollContainerRef.current.style.cursor = 'grab';
    };

    const onMouseUp = () => {
        isDown.current = false;
        scrollContainerRef.current.style.cursor = 'grab';
        // Reset dragging flag after a short delay to allow click events to process correctly if it wasn't a drag
        setTimeout(() => {
            isDragging.current = false;
        }, 50);
    };

    const onMouseMove = (e) => {
        if (!isDown.current) return;
        e.preventDefault();
        const x = e.pageX - scrollContainerRef.current.offsetLeft;
        const walk = (x - startX.current) * 2; // Scroll-fast
        
        // If moved significantly, mark as dragging to prevent click
        if (Math.abs(walk) > 5) {
            isDragging.current = true;
        }
        
        scrollContainerRef.current.scrollLeft = scrollLeft.current - walk;
    };

    const handleCardClick = (date) => {
        if (isDragging.current) return;

        const dateKey = format(date, 'yyyy-MM-dd');
        const plan = mealPlans[dateKey];
        setSelectedDate(date);
        
        // If no meal, auto-start recording
        if (!plan || !plan.meal) {
            setAutoStart(true);
        } else {
            setAutoStart(false);
        }
        
        setIsModalOpen(true);
    };

    const handleSaveMeal = async (date, mealText, chef) => {
        if (!date) return;
        
        const dateKey = format(date, 'yyyy-MM-dd');
        const docRef = doc(db, 'meal_plans', dateKey);
        
        try {
            await setDoc(docRef, {
                meal: mealText,
                chef: chef,
                date: dateKey,
                updatedAt: new Date().toISOString()
            }, { merge: true });
            
            enqueueSnackbar('Meal saved!', { variant: 'success' });
        } catch (error) {
            console.error("Error saving meal plan:", error);
            enqueueSnackbar('Failed to save meal plan.', { variant: 'error' });
        }
    };

    const handleMicClick = (date) => {
        if (isDragging.current) return;
        
        // Direct mic click opens modal and auto-triggers? 
        // For now, let's just open the modal. User can tap mic there.
        // Or we could pass a flag to auto-start.
        setSelectedDate(date);
        setIsModalOpen(true);
    };

    return (
        <>
            <Helmet>
                <title>Meal Planning</title>
            </Helmet>

            <Box sx={{ 
                height: '100vh', 
                width: '100vw', 
                display: 'flex', 
                flexDirection: 'column',
                bgcolor: '#48bb78', // Matching Command Center Tile
                overflow: 'hidden'
            }}>
                {/* Header */}
                <Box sx={{ p: 4 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <IconButton 
                            onClick={handleBack}
                            size="large"
                            sx={{ 
                                bgcolor: 'white', 
                                boxShadow: 2,
                                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' }
                            }}
                        >
                            <ArrowBackIcon fontSize="large" sx={{ color: '#48bb78' }} />
                        </IconButton>
                        <Typography 
                            variant="h3" 
                            fontWeight="900" 
                            color="white"
                            sx={{ 
                                textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                fontSize: { xs: '2rem', md: '3rem' }
                            }}
                        >
                            Meal Planning
                        </Typography>
                    </Stack>
                </Box>

                {/* Main Content - Horizontal Scroll */}
                <Box 
                    ref={scrollContainerRef}
                    onMouseDown={onMouseDown}
                    onMouseLeave={onMouseLeave}
                    onMouseUp={onMouseUp}
                    onMouseMove={onMouseMove}
                    sx={{ 
                        flex: 1, 
                        overflowX: 'auto', 
                        overflowY: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        px: 4,
                        pb: 8,
                        gap: 2,
                        scrollSnapType: 'x mandatory',
                        '&::-webkit-scrollbar': { display: 'none' },
                        msOverflowStyle: 'none',
                        scrollbarWidth: 'none',
                        userSelect: 'none',
                        cursor: 'grab'
                    }}
                >
                    {weekDays.map((date) => {
                        const dateKey = format(date, 'yyyy-MM-dd');
                        const plan = mealPlans[dateKey] || {};
                        
                        return (
                            <MealDayCard
                                key={dateKey}
                                date={date}
                                meal={plan.meal}
                                chef={plan.chef}
                                onClick={() => handleCardClick(date)}
                                onMicClick={() => handleMicClick(date)}
                            />
                        );
                    })}
                </Box>
            </Box>

            {/* Edit Modal */}
            {selectedDate && (
                <MealEditModal
                    open={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    date={selectedDate}
                    initialMeal={mealPlans[format(selectedDate, 'yyyy-MM-dd')]?.meal}
                    initialChef={mealPlans[format(selectedDate, 'yyyy-MM-dd')]?.chef}
                    onSave={handleSaveMeal}
                    autoStartRecording={autoStart}
                />
            )}
        </>
    );
};

export default MealPlanning;
